/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSelector } from 'reselect';
import { compose, prop, isNil } from 'ramda';
import { getCustomSuccessInfo } from '@/components/ErrorBoundary/customMessageUtil';

import { MONITOR_ACTIONS as ACTIONS, APPLICATION_ACTION, PRODUCT_ACTION, USER_ACTION } from '../actions';

const initialState = {
  error: null,
  remoteCommandResponse: {
    responseCode: '',
    message: '',
  },
  successInfo: null,
  errorCodes: [],
};

// Helper function to extract serializable error data while preserving structure
const serializeError = (error: any) => {
  if (!error) return null;
  
  const headersToObject = (headers: any) => {
    if (!headers) return undefined;
    if (headers.constructor === Object) return headers;
    if (headers.toJSON && typeof headers.toJSON === 'function') {
      return headers.toJSON();
    }
    const plainHeaders: any = {};
    for (const key in headers) {
      if (typeof headers[key] !== 'function') {
        plainHeaders[key] = headers[key];
      }
    }
    return plainHeaders;
  };
  
  return {
    message: error.message,
    code: error.code,
    name: error.name,
    stack: error.stack,
    config: error.config ? {
      url: error.config.url,
      method: error.config.method,
      baseURL: error.config.baseURL,
      headers: headersToObject(error.config.headers),
      data: error.config.data,
      params: error.config.params,
    } : undefined,
    response: error.response ? {
      data: error.response.data,
      status: error.response.status,
      statusText: error.response.statusText,
      headers: headersToObject(error.response.headers),
    } : undefined,
    request: error.request ? {} : undefined, // Request object is not serializable, keep empty
  };
};

const transformError = (error: { message?: any; config?: any; response?: any }) => {
  if (!error) return null;
  const { config } = error;
  const api = config ? `${config.method} ${config.url}` : '';
  const errorResponse = error.response?.data || error.message;

  if (error) {
    return {
      api,
      code: '-1',
      message: errorResponse ? errorResponse : error,
    };
  }

  return null;
};

const transformSuccessInfo = (successInfo: any) => {
  if (isNil(successInfo)) return null;
  return successInfo;
};

const getSlice = prop('monitor');

export const getError = createSelector(getSlice, compose(transformError, prop('error')));

export const getErrorCodes = createSelector(getSlice, prop('errorCodes'));

export const getSucccessInfo = createSelector(getSlice, compose(transformSuccessInfo, prop('successInfo')));

export const closeError = () => ({
  type: ACTIONS.CLOSE_ERROR,
});

export const closeSuccessInfo = () => ({
  type: ACTIONS.CLOSE_SUCCESS_INFO,
});

export default (state = initialState, { type, ...action } = {}) => {
  switch (type) {
    case ACTIONS.CLOSE_ERROR:
      return {
        ...state,
        error: null,
      };
    case ACTIONS.CLOSE_SUCCESS_INFO:
      return {
        ...state,
        successInfo: null,
      };
    case USER_ACTION.SIGNIN_SUCCESS:
    case USER_ACTION.LOGIN_SUCCESS:
    case USER_ACTION.PASSWORD_RESET_SUCCESS:
    case USER_ACTION.PASSWORD_RESET_CONFIRM_SUCCESS:
    case APPLICATION_ACTION.CONTACT_SUCCESS:
    case APPLICATION_ACTION.SUBSCRIBE_SUCCESS:
    case APPLICATION_ACTION.EVENTREG_SUCCESS:
    case PRODUCT_ACTION.PRODUCTENQ_SUCCESS:
      return {
        ...state,
        error: null,
        successInfo: getCustomSuccessInfo(type, action),
      };

    case USER_ACTION.SIGNIN_FAILURE:
    case USER_ACTION.LOGIN_FAILURE:
    case USER_ACTION.PASSWORD_RESET_FAILURE:
    case USER_ACTION.PASSWORD_RESET_CONFIRM_FAILURE:
    case APPLICATION_ACTION.CONTACT_FAILURE:
    case APPLICATION_ACTION.SUBSCRIBE_FAILURE:
    case PRODUCT_ACTION.PRODUCTENQ_FAILURE:
      return {
        ...state,
        error: serializeError(action?.error),
        successInfo: null,
      };
    default:
      return state;
  }
};